import Yggdrasil from 'services/yggdrasil'
import Vue from 'vue'
import moment from 'moment'
import {
    combineFiltersWithFacets,
    initObjectWithKeysAndArrayAsDefaultValue,
    doSearchDefaultTimeoutDuration,
    searchDebounceDuration
} from '../shared/filtersHelper'

let currentSearchController
let doSearchTimeout = null
let singleStringValues = ['startDate', 'endDate']

export const filterKeys = [
    'courseType',
    'memberStatus',
    'memberCompany',
    'memberDepartment',
    'memberTeam',
    'courseAuthorization',
    'startDate',
    'endDate'
]

const state = {
    searchEnabled: false,
    filters: {
        ...initObjectWithKeysAndArrayAsDefaultValue(filterKeys),
        memberStatus: [{ value: 'active', label: ('ReportingFilters.status_active') }]
    },
    facets: initObjectWithKeysAndArrayAsDefaultValue(filterKeys),
    filtersWithFacets: initObjectWithKeysAndArrayAsDefaultValue(filterKeys),
    page: 1,
    pageCount: 0,
    pageSize: 20,
    rowCount: 0,
    searchQuery: '',
    foundItems: [],
    dashboard: {},
    totalHoursWatched: [],
    mostActiveUsers: [],
    filterStaticData: {
        startDate: {
            filterKey: 'startDate',
            translationString: 'General.start_date',
            initialValue: null,
            order: 1
        },
        endDate: {
            filterKey: 'endDate',
            translationString: 'General.end_date',
            initialValue: moment().subtract(1, 'days').format('YYYY-MM-DD'),
            order: 2
        },
        memberStatus: {
            filterKey: 'memberStatus',
            translationString: 'ReportingFilters.status',
            order: 3
        },
        memberCompany: {
            filterKey: 'memberCompany',
            translationString: 'ReportingFilters.company',
            order: 4
        },
        memberDepartment: {
            filterKey: 'memberDepartment',
            translationString: 'ReportingFilters.department',
            order: 5
        },
        memberTeam: {
            filterKey: 'memberTeam',
            translationString: 'ReportingFilters.team',
            order: 6
        },
        courseAuthorization: {
            filterKey: 'courseAuthorization',
            translationString: 'ReportingFilters.authorization',
            order: 7
        },
        courseType: {
            filterKey: 'courseType',
            translationString: 'ReportingFilters.course_type',
            order: 8
        }
    },
    searchInProgress: false,
    members: {},
    memberCourses: [],
    memberDetails: {},
    orderBy: {
        column: '',
        direction: ''
    },
    reportingConfiguration: {}
}

const mutations = {
    SET_SEARCH_RESULTS (state, results) {
        state.foundItems = results.members
        state.page = results.pagination.page
        state.pageCount = results.pagination.pageCount
        state.pageSize = results.pagination.pageSize
        state.rowCount = results.pagination.rowCount
    },
    SET_DASHBOARD_RESULTS (state, results) {
        Object.keys(state.facets).forEach(facetKey => {
            state.facets[facetKey] = results.filterFacets[facetKey] ?? []
        })
        state.filtersWithFacets = combineFiltersWithFacets(state.filters, state.facets, filterKeys)
        state.dashboard = {
            activeUsers: results.dashboard.activeUsers,
            averageWatchTime: results.dashboard.averageMinutesWatched,
            totalUsers: results.dashboard.totalUsers,
            totalWatchTime: results.dashboard.totalMinutesWatched
        }
        state.totalHoursWatched = results.dashboard.graph
        state.mostActiveUsers = results.dashboard.mostActiveUsers
    },
    SET_QUERY (state, query) {
        state.page = 1
        state.searchQuery = query
    },
    ADD_FILTER_VALUE (state, valueOptions) {
        if (singleStringValues.includes(valueOptions.key)) {
            state.filters[valueOptions.key] = []
        }
        state.filters[valueOptions.key].push({ value: valueOptions.value, label: valueOptions.label })
    },
    REMOVE_FILTER_VALUE (state, valueOptions) {
        state.filters[valueOptions.key] = state.filters[valueOptions.key].filter(filterValue => filterValue.value !== valueOptions.value)
    },
    SET_FILTER_VALUES (state, valueOptions) {
        let filters = valueOptions.filters.map(option => {
            return {
                value: option.id,
                label: option.labelWithoutCount
            }
        })
        Vue.set(state.filters, valueOptions.key, filters)
    },
    CLEAR_ALL_FILTERS (state) {
        state.filters = {
            ...initObjectWithKeysAndArrayAsDefaultValue(filterKeys),
            startDate: [
                {
                    value: state.filterStaticData.startDate.initialValue,
                    label: state.filterStaticData.startDate.initialValue
                }],
            endDate: [
                {
                    value: state.filterStaticData.endDate.initialValue,
                    label: state.filterStaticData.endDate.initialValue
                }]
        }
    },
    CLEAR_FILTER (state, key) {
        if (state.filters[key]) {
            Vue.set(state.filters, key, [])
        }
    },
    RESET_PAGINATION (state) {
        state.page = 1
    },
    SET_SEARCH_ENABLED (state, searchEnabled) {
        state.searchEnabled = searchEnabled
    },
    SET_SEARCH_IN_PROGRESS (state, searchInProgress) {
        state.searchInProgress = searchInProgress
    },
    PAGE_NUMBER_CHANGE (state, pageIndex) {
        state.page = pageIndex
    },
    PAGE_SIZE_CHANGE (state, pageSize) {
        state.pageSize = pageSize
    },
    SORT_ORDER_CHANGE (state, sortParams) {
        const key = Object.keys(sortParams).filter(key => !!sortParams[key]).toString()
        state.orderBy.column = key
        state.orderBy.direction = sortParams[key]
    },
    SET_MEMBER_DETAILS_RESULT (state, memberDetails) {
        state.memberDetails = memberDetails.member
        state.memberCourses = memberDetails.courses
    },
    SET_REPORTING_CONFIGURATION (state, configuration) {
        if (configuration === undefined) {
            return
        }
        state.reportingConfiguration = configuration
        state.filterStaticData.startDate.initialValue = moment(configuration.minDate).format('YYYY-MM-DD')
        state.orderBy.column = configuration.orderByDefaults.membersTable.column
        state.orderBy.direction = configuration.orderByDefaults.membersTable.direction
    },
    CLEAR_AFTER_LOGOUT (state) {
        state.searchQuery = ''
        state.foundItems = []
        state.reportingConfiguration = {}
        state.dashboard = {}
        state.memberDetails = {}
        state.memberCourses = []
        state.totalHoursWatched = []
        state.mostActiveUsers = []
        state.filters = initObjectWithKeysAndArrayAsDefaultValue(filterKeys)
        state.facets = initObjectWithKeysAndArrayAsDefaultValue(filterKeys)
        state.filtersWithFacets = initObjectWithKeysAndArrayAsDefaultValue(filterKeys)
    }
}

function compiledFilter (state) {
    const compiledFilter = {
        pagination: {},
        filter: {
            startDate: null,
            endDate: null,
            memberQuery: '',
            memberTeam: [],
            memberCompany: [],
            memberDepartment: [],
            memberStatus: null,
            courseType: [],
            courseAuthorization: [],
            hideCompletedCourses: false,
            hideUncompletedCourses: false
        },
        orderBy: {
            column: '',
            direction: ''
        }
    }
    Object.keys(state.filters).forEach(filterKey => {
        if (!state.filters[filterKey].length) {
            return
        }
        if (singleStringValues.includes(filterKey)) {
            compiledFilter.filter[filterKey] = state.filters[filterKey].map(el => el.value).toString()
            return
        }

        if (filterKey === 'memberStatus') {
            (state.filters[filterKey].length === 2 || state.filters[filterKey].length === 0)
                ? compiledFilter.filter[filterKey] = null
                : compiledFilter.filter[filterKey] = state.filters[filterKey].map(el => el.value).toString()
            return
        }

        compiledFilter.filter[filterKey] = state.filters[filterKey].map(el => el.value)
    })
    if (state.searchQuery) {
        compiledFilter.filter.memberQuery = state.searchQuery
    }
    compiledFilter.pagination.pageSize = state.pageSize
    compiledFilter.pagination.page = state.page
    compiledFilter.orderBy.column = state.orderBy.column
    compiledFilter.orderBy.direction = state.orderBy.direction

    return compiledFilter
}

const actions = {
    initializeConfiguration (store, configuration) {
        store.commit('SET_REPORTING_CONFIGURATION', configuration)
    },
    initSearch (store) {
        store.dispatch('doSearch')
    },
    setQuery (store, query) {
        if (store.state.searchQuery === query) {
            return
        }
        store.commit('RESET_PAGINATION')
        store.commit('SET_QUERY', query)
        store.dispatch('doSearch')
    },
    addFilterValue (store, valueOptions) {
        store.commit('RESET_PAGINATION')
        store.commit('ADD_FILTER_VALUE', valueOptions)
        store.dispatch('doSearch')
    },
    removeFilterValue (store, valueOptions) {
        store.commit('RESET_PAGINATION')
        store.commit('REMOVE_FILTER_VALUE', valueOptions)
        store.dispatch('doSearch')
    },
    setFilterValues (store, payload) {
        store.commit('RESET_PAGINATION')
        store.commit('SET_FILTER_VALUES', payload)
        store.dispatch('doSearch')
    },
    clearAllFilters (store) {
        store.commit('RESET_PAGINATION')
        store.commit('CLEAR_ALL_FILTERS')
        store.commit('SET_QUERY', '')
        store.dispatch('doSearch')
    },
    clearFilter (store, { key }) {
        store.commit('RESET_PAGINATION')
        store.commit('CLEAR_FILTER', key)
        store.dispatch('doSearch')
    },
    pageNumberChange (store, pageIndex) {
        store.commit('PAGE_NUMBER_CHANGE', pageIndex)
        store.dispatch('doSearch', { getDashboardResponse: false })
    },
    pageSizeChange (store, pageSize) {
        store.commit('PAGE_SIZE_CHANGE', pageSize)
        store.dispatch('doSearch', { getDashboardResponse: false })
    },
    sortOrderChange (store, sortParams) {
        store.commit('SORT_ORDER_CHANGE', sortParams)
        store.dispatch('doSearch', { getDashboardResponse: false })
    },
    async doSearch (store, options = { timeout: doSearchDefaultTimeoutDuration, getDashboardResponse: true }) {
        clearTimeout(doSearchTimeout)
        doSearchTimeout = setTimeout(() => store.dispatch('doSearchNow', options.getDashboardResponse), options.timeout)
    },
    async doSearchNow (store, getDashboardResponse = true) {
        store.commit('SET_SEARCH_IN_PROGRESS', true)
        if (currentSearchController) {
            currentSearchController.abort()
        }
        clearTimeout(doSearchTimeout)
        if (store.state.searchEnabled !== true) {
            return
        }
        doSearchTimeout = setTimeout(async () => {
            try {
                currentSearchController = new AbortController()
                await store.dispatch('LoaderScreen/addTaskToLoader', {
                    key: 'search',
                    message: 'Loading data'
                }, { root: true })
                const searchResult = await Yggdrasil.loadMembersForReporting(compiledFilter(store.state))
                store.commit('SET_SEARCH_RESULTS', searchResult)

                if (getDashboardResponse) {
                    const dashboardResult = await Yggdrasil.loadMembersDashboardForReporting(compiledFilter(store.state))
                    store.commit('SET_DASHBOARD_RESULTS', dashboardResult)
                }
                store.commit('SET_SEARCH_IN_PROGRESS', false)
            } catch (e) {
            }
            store.commit('SET_SEARCH_IN_PROGRESS', false)
            store.dispatch('LoaderScreen/removeTaskFromLoader', 'search', { root: true })
        }, searchDebounceDuration)
    },
    enableSearch (store) {
        store.commit('SET_SEARCH_ENABLED', true)
    },
    disableSearch (store) {
        store.commit('SET_SEARCH_ENABLED', false)
    },
    async loadMemberDetailsForReporting (store, memberId) {
        const { filter, ...rest } = compiledFilter(store.state)
        const filters = {}
        filters.filter = { ...filter }

        const result = await Yggdrasil.loadMemberDetailsForReporting(memberId, filters)
        store.commit('SET_MEMBER_DETAILS_RESULT', result)
    },
    async loadUsersReportingExcel (store) {
        await Yggdrasil.downloadMembersReportingExcel(compiledFilter(store.state))
    },
    async clearAfterLogout (store) {
        store.commit('CLEAR_AFTER_LOGOUT')
    }
}

const getters = {
    searchQuery: state => state.searchQuery,
    facets: state => state.facets,
    filters: state => state.filters,
    filterWithKey: (state) => (key) => state.filters[key],
    searchResults: state => state.foundItems,
    filtersWithFacets: (state) => state.filtersWithFacets,
    tags (store, getters) {
        return Object.keys(getters.filtersWithFacets).reduce((tags, filterKey) => {
            return tags.concat(
                getters.filtersWithFacets[filterKey]
                    .filter(filterItem => filterItem.selected)
                    .map(filterItem => ({
                        value: filterItem.value,
                        name: filterItem.label,
                        count: filterItem.count,
                        filterKey: filterKey,
                        order: getters.getFilterStaticData[filterKey].order
                    })
                    ))
        }, [])
    },
    getFilterStaticData: (state) => state.filterStaticData,
    isSearchInProgress: (state) => state.searchInProgress,
    rowCount: (state) => state.rowCount,
    getPage: (state) => state.page,
    getDashboard: (state) => state.dashboard,
    getTotalHoursWatched: (state) => state.totalHoursWatched,
    getMostActiveUsers: (state) => state.mostActiveUsers,
    getMemberDetails: (state) => state.memberDetails,
    getMemberCourses: (state) => state.memberCourses,
    getReportingConfiguration: (state) => state.reportingConfiguration,
    isThereAnyActiveFilter: (state) => {
        const activeFilters = {}
        if (state.searchQuery) {
            return true
        }
        Object.keys(state.filters).forEach((filter) => {
            if (filter === 'startDate' || filter === 'endDate') {
                if ((state.filters[filter][0].value !== state.filterStaticData[filter].initialValue)) {
                    activeFilters[filter] = state.filters[filter]
                }
            } else if (state.filters[filter].length > 0) {
                activeFilters[filter] = state.filters[filter]
            }
        })
        return Object.keys(activeFilters).length !== 0
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
